import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle } from 'react-feather';
import { CSSTransition } from 'react-transition-group';
import { createCompareFn } from 'utils/sort';
import { escapeRegex } from 'utils/string_utils';
import request from 'utils/request_ajax';
import InfoTooltip from 'components/shared/InfoTooltip';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import { LabeledCheckbox } from 'components/shared/checkbox';
import TextField from 'ui/TextField';
import Chip from 'ui/Chip';

const DISALLOWED_KEYWORD_CHARACTERS = '|(){}[]^"~*?!$:/\\';

const DEFAULT_ERROR_STATE = {
  duplicate: false,
  symbolValidation: null,
  unableToAdd: false,
};

const KeywordMatching = ({
  content,
  getAutomatedAnalysisKeywordsPath,
  addAutomatedAnalysisKeywordPath,
  removeAutomatedAnalysisKeywordPath,
  onChange,
  keywordsNull,
  setKeywordsNull,
}) => {
  const [correctKeywordsErrors, setCorrectKeywordsErrors] =
    useState(DEFAULT_ERROR_STATE);
  const [incorrectKeywordsErrors, setIncorrectKeywordsErrors] =
    useState(DEFAULT_ERROR_STATE);
  const [correctKeywords, setCorrectKeywords] = useState([]);
  const [incorrectKeywords, setIncorrectKeywords] = useState([]);
  const [correctKeywordValue, setCorrectKeywordValue] = useState('');
  const [incorrectKeywordValue, setIncorrectKeywordValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabledState, setIsDisabledState] = useState(false);
  const [correctKeywordCheck, setCorrectKeywordCheck] = useState('');
  const [incorrectKeywordCheck, setIncorrectKeywordCheck] = useState('');

  const [removeUnsavedChip, setRemoveUnsavedChip] = useState('');
  const [fieldToFocus, setFieldToFocus] = useState('');
  const [disableKeywordMatchingArea, setDisableKeywordMatchingArea] = useState(
    content.automated_analysis_keyword_matching_enabled,
  );

  const showCorrectKeywordError =
    correctKeywordsErrors.duplicate ||
    correctKeywordsErrors.symbolValidation ||
    correctKeywordsErrors.unableToAdd;
  const showIncorrectKeywordError =
    incorrectKeywordsErrors.duplicate ||
    incorrectKeywordsErrors.symbolValidation ||
    incorrectKeywordsErrors.unableToAdd;

  const correctInputRef = useRef();
  const incorrectInputRef = useRef();

  const onChangeKeywordMatching = e => {
    setTimeout(
      onChange({
        question_automated_analysis_keyword_matching_enabled: e.target.checked,
        automated_analysis_keyword_matching_enabled: e.target.checked,
      }),
      setDisableKeywordMatchingArea(prev => !prev),
      900,
    );
  };

  const onKeywordRemoved = (correctKeywordsArray, incorrectKeywordsArray) => {
    if (!correctKeywordsArray.length && !incorrectKeywordsArray.length) {
      onChange({
        question_automated_analysis_keyword_matching_enabled: false,
        automated_analysis_keyword_matching_enabled: false,
      });
      setDisableKeywordMatchingArea(false);
    }
  };

  function hasInvalidSymbols(keyword, isCorrectKeywordArray) {
    const invalidSymbols = [
      ...new Set(
        [
          ...keyword.matchAll(
            new RegExp(
              '[' + escapeRegex(DISALLOWED_KEYWORD_CHARACTERS) + ']',
              'g',
            ),
          ),
        ].flat(),
      ),
    ].join('');

    if (invalidSymbols !== '') {
      if (isCorrectKeywordArray) {
        setCorrectKeywordsErrors(prev => ({
          ...prev,
          symbolValidation: invalidSymbols,
        }));
      } else {
        setIncorrectKeywordsErrors(prev => ({
          ...prev,
          symbolValidation: invalidSymbols,
        }));
      }
      return true;
    }
    return false;
  }

  function isDuplicateKeyword(keyword, isCorrectKeywordArray) {
    let isValid = true;
    correctKeywords
      .map(ck => ck.text)
      .forEach(correctKeyword => {
        if (keyword.toUpperCase() === correctKeyword.toUpperCase()) {
          if (isCorrectKeywordArray) {
            setCorrectKeywordsErrors(prev => ({
              ...prev,
              duplicate: t(
                'automated_analysis.keyword_matching.errors.duplicate_keyword.correct',
              ),
            }));
          } else {
            setIncorrectKeywordsErrors(prev => ({
              ...prev,
              duplicate: t(
                'automated_analysis.keyword_matching.errors.duplicate_keyword_remove.correct',
              ),
            }));
          }

          isValid = false;
        }
      });
    incorrectKeywords
      .map(ik => ik.text)
      .forEach(incorrectKeyword => {
        if (keyword.toUpperCase() === incorrectKeyword.toUpperCase()) {
          if (isCorrectKeywordArray) {
            setCorrectKeywordsErrors(prev => ({
              ...prev,
              duplicate: t(
                'automated_analysis.keyword_matching.errors.duplicate_keyword_remove.incorrect',
              ),
            }));
          } else {
            setIncorrectKeywordsErrors(prev => ({
              ...prev,
              duplicate: t(
                'automated_analysis.keyword_matching.errors.duplicate_keyword.incorrect',
              ),
            }));
          }
          isValid = false;
        }
      });
    return isValid;
  }

  const ErrorText = ({ error }) => {
    return (
      <div className="mt-8 mb-8">
        <Text
          className="d-f ai-c"
          variant={TEXT_VARIANTS.INSTRUX_SMALL_SEMIBOLD_ERROR}
        >
          <AlertCircle className="mr-4" size={14} />
          {error.symbolValidation &&
            t('automated_analysis.keyword_matching.errors.invalid_characters', {
              count: error.symbolValidation.length,
            }) + ` ${error.symbolValidation}`}
          {!error.symbolValidation && error.duplicate}
          {error.unableToAdd &&
            t('automated_analysis.keyword_matching.errors.unable_to_add')}
        </Text>
      </div>
    );
  };

  async function getKeywords() {
    try {
      await request.get(getAutomatedAnalysisKeywordsPath).then(response => {
        const keywords = response;
        setCorrectKeywords(
          keywords
            ?.filter(keyword => keyword?.allowed)
            .sort(createCompareFn('id')),
        );

        setIncorrectKeywords(
          keywords
            ?.filter(keyword => !keyword?.allowed)
            .sort(createCompareFn('id')),
        );
      });
    } catch (error) {
      console.error(error);
    }
  }

  async function removeKeyword(keyword, isCorrect) {
    setIsDisabledState(true);
    setIsLoading(true);
    await request.postJSON(removeAutomatedAnalysisKeywordPath, {
      text: keyword,
      correct: isCorrect,
    });
    setIsLoading(false);
  }

  async function addKeyword(keyword, isCorrect) {
    setFieldToFocus(isCorrect ? 'correct' : 'incorrect');
    setIsDisabledState(true);
    setIsLoading(true);
    try {
      const newKeyword = await request.postJSON(
        addAutomatedAnalysisKeywordPath,
        {
          text: keyword,
          correct: isCorrect,
        },
      );
      (isCorrect ? setCorrectKeywords : setIncorrectKeywords)([
        ...(isCorrect ? correctKeywords : incorrectKeywords),
        {
          id: newKeyword.id,
          text: keyword,
          correct: isCorrect,
        },
      ]);
      // await getKeywords();
      setIsLoading(false);
      setIncorrectKeywordCheck('');
      setCorrectKeywordCheck('');
      onChange({
        some_unused_property_that_will_be_excluded_by_allowed_params_but_will_ensure_lesson_updates_its_publishable_property: true,
      });
    } catch (error) {
      if (isCorrect) {
        setCorrectKeywordsErrors({
          unableToAdd: true,
        });
      } else {
        setIncorrectKeywordsErrors({
          unableToAdd: true,
        });
      }
      setIsLoading(false);
      setRemoveUnsavedChip(isCorrect ? 'correct' : 'incorrect');
    }
  }

  // On initial render, load keywords
  useEffect(() => {
    setIsLoading(true);
    getKeywords().finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isDisabledState) {
      setTimeout(() => {
        setIsDisabledState(false);
      }, 1000);
    } else if (!isLoading && fieldToFocus === 'correct') {
      correctInputRef.current?.focus();
    } else if (!isLoading && fieldToFocus === 'incorrect') {
      incorrectInputRef.current?.focus();
    }
  }, [isDisabledState, isLoading, fieldToFocus]);

  useEffect(() => {
    if (removeUnsavedChip === 'correct') {
      setCorrectKeywords(correctKeywords.slice(0, correctKeywords.length - 1));
      setRemoveUnsavedChip('');
    } else if (removeUnsavedChip === 'incorrect') {
      setIncorrectKeywords(
        incorrectKeywords.slice(0, incorrectKeywords.length - 1),
      );
      setRemoveUnsavedChip('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeUnsavedChip]);

  useEffect(() => {
    if (keywordsNull) {
      setCorrectKeywords([]);
      setIncorrectKeywords([]);
      setCorrectKeywordValue('');
      setIncorrectKeywordValue('');
      setKeywordsNull(false);
    }
    if (correctKeywordValue && !hasInvalidSymbols(correctKeywordValue, true)) {
      setCorrectKeywordsErrors({
        duplicate: false,
        symbolValidation: null,
      });
    } else if (
      incorrectKeywordValue &&
      !hasInvalidSymbols(incorrectKeywordValue, false)
    ) {
      setIncorrectKeywordsErrors({
        duplicate: false,
        symbolValidation: null,
      });
    }
  }, [
    incorrectKeywords,
    correctKeywords,
    correctKeywordValue,
    incorrectKeywordValue,
    keywordsNull,
    setKeywordsNull,
  ]);

  return (
    <>
      <div
        className="d-f fd-c"
        data-testid={`KeywordMatching${!isLoading && '-loaded'}`}
      >
        <LabeledCheckbox
          className="EditorElement-options mt-20 w-fc"
          checkBoxClassName="va-m h-18 w-18 mr-12-imp"
          label={
            <>
              <Text variant={TEXT_VARIANTS.BODY} className="as-b">
                {t('automated_analysis.keyword_matching.title')}
              </Text>
              <InfoTooltip
                className="pl-4 mt-4 tooltip-fixedWidth c-gray-light"
                tooltip={t('automated_analysis.keyword_matching.tooltip')}
              />
            </>
          }
          onChange={e => onChangeKeywordMatching(e)}
          checked={content.automated_analysis_keyword_matching_enabled}
        />
        <CSSTransition
          appear
          classNames="sliderKeywordMatching"
          in={disableKeywordMatchingArea}
          timeout={700}
          unmountOnExit
          onExited={() => setDisableKeywordMatchingArea(false)}
        >
          <div>
            <div className="sliderKeywordMatching ml-32">
              <label className="mt-16 mb-0" htmlFor="correct_keywords_label">
                <Text variant={TEXT_VARIANTS.INSTRUX_SMALL_SEMIBOLD}>
                  {t(
                    'automated_analysis.keyword_matching.correct_keywords_label',
                  )}
                </Text>
              </label>
              {!correctKeywords.length && !incorrectKeywords.length && (
                <>
                  <span className="c-danger-red"> * </span>
                </>
              )}

              <TextField
                className="EditorElement-input EditorElement-input--single EditorElement-input--answer-key"
                disabled={isDisabledState || isLoading}
                required
                placeholder={t(
                  'automated_analysis.keyword_matching.input_placeholder',
                )}
                value={correctKeywordValue}
                onKeyDown={e => {
                  const trimmedKeyword = correctKeywordValue.trim();
                  if (
                    (e.keyCode === 13 || e.key === 'Enter') &&
                    trimmedKeyword !== '' &&
                    !showCorrectKeywordError
                  ) {
                    if (isDuplicateKeyword(trimmedKeyword, true)) {
                      setCorrectKeywordCheck(trimmedKeyword);
                      addKeyword(trimmedKeyword, true);

                      setCorrectKeywords(prev => [
                        ...prev,
                        { text: trimmedKeyword, allowed: true },
                      ]);
                      setCorrectKeywordValue('');
                    }
                  }
                }}
                onChange={e => {
                  if (!hasInvalidSymbols(e.target.value, true)) {
                    setCorrectKeywordsErrors({
                      duplicate: false,
                      symbolValidation: null,
                    });
                  }
                  setCorrectKeywordValue(e.target.value);
                }}
                ref={correctInputRef}
              />
              {showCorrectKeywordError && (
                <ErrorText error={correctKeywordsErrors} />
              )}
              <div className="fw-w d-f fd-r">
                {correctKeywords?.map((keyword, index) => (
                  <div key={keyword.text} className="mt-8 mr-8">
                    <Chip
                      ariaLabel="remove-correct-keyword"
                      size="small"
                      variant="default"
                      isDisabled={
                        isDisabledState && keyword.text === correctKeywordCheck
                      }
                      isLoading={
                        isLoading && keyword.text === correctKeywordCheck
                      }
                      value={keyword.text}
                      onClick={async () => {
                        if (!isLoading) {
                          setCorrectKeywordCheck(keyword.text);
                          const tempArray = [...correctKeywords];
                          tempArray.splice(index, 1);
                          await removeKeyword(keyword.text, true);
                          setCorrectKeywords(tempArray);
                          onKeywordRemoved(tempArray, incorrectKeywords);
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="sliderKeywordMatching ml-32">
              <label className="mt-16 mb-0" htmlFor="incorrect_keywords_label">
                <Text variant={TEXT_VARIANTS.INSTRUX_SMALL_SEMIBOLD}>
                  {t(
                    'automated_analysis.keyword_matching.incorrect_keywords_label',
                  )}
                </Text>
              </label>
              {!correctKeywords.length && !incorrectKeywords.length && (
                <>
                  <span className="c-danger-red"> * </span>
                </>
              )}

              <TextField
                className="EditorElement-input EditorElement-input--single EditorElement-input--answer-key"
                disabled={isDisabledState || isLoading}
                required
                placeholder={t(
                  'automated_analysis.keyword_matching.input_placeholder',
                )}
                value={incorrectKeywordValue}
                onKeyDown={e => {
                  const trimmedKeyword = incorrectKeywordValue.trim();
                  if (
                    (e.keyCode === 13 || e.key === 'Enter') &&
                    trimmedKeyword !== '' &&
                    !showIncorrectKeywordError
                  ) {
                    if (isDuplicateKeyword(trimmedKeyword, false)) {
                      setIncorrectKeywordCheck(trimmedKeyword);
                      addKeyword(trimmedKeyword, false);

                      setIncorrectKeywords(prev => [
                        ...prev,
                        { text: trimmedKeyword, allowed: false },
                      ]);
                      setIncorrectKeywordValue('');
                    }
                  }
                }}
                onChange={e => {
                  if (!hasInvalidSymbols(e.target.value, false)) {
                    setIncorrectKeywordsErrors({
                      duplicate: false,
                      symbolValidation: null,
                    });
                  }
                  setIncorrectKeywordValue(e.target.value);
                }}
                ref={incorrectInputRef}
              />

              {showIncorrectKeywordError && (
                <ErrorText error={incorrectKeywordsErrors} />
              )}
              <div className="fw-w d-f fd-r">
                {incorrectKeywords?.map((keyword, index) => (
                  <div key={keyword.text} className="mt-8 mr-8">
                    <Chip
                      size="small"
                      variant="default"
                      ariaLabel="remove-incorrect-keyword"
                      isDisabled={
                        isDisabledState &&
                        keyword.text === incorrectKeywordCheck
                      }
                      isLoading={
                        isLoading && keyword.text === incorrectKeywordCheck
                      }
                      value={keyword.text}
                      onClick={async () => {
                        if (!isLoading) {
                          setIncorrectKeywordCheck(keyword.text);
                          const tempArray = [...incorrectKeywords];
                          tempArray.splice(index, 1);
                          await removeKeyword(keyword.text, false);
                          setIncorrectKeywords(tempArray);
                          onKeywordRemoved(correctKeywords, tempArray);
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CSSTransition>
      </div>
    </>
  );
};

export default KeywordMatching;
